import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { debounce } from 'throttle-debounce';

import Input from '@welldev-group/rcl-input';
import Icon from '@welldev-group/rcl-icon';
import {
  BreadcrumbItem,
  Breadcrumbs,
  I18nText,
  SortingPill,
} from '@welldev-group/react-comp-lib';

import PaginationWithTranslation from '../../../shared/components/Pagination/PaginationWithTranslation';
import httpClient from '../../../shared/libraries/httpClient';
import routes from '../../../shared/routes';

import ListTable from './ListTable';
import TeamModal from './TeamModal';

const TeamIndex = props => {
  const [affiliates, setAffiliates] = useState([]);
  const [searchParam, setSearchParam] = useState('');
  const [sortDirection, setSortDirection] = useState(null);
  const [currentAffiliatePage, setCurrentAffiliatePage] = useState(1);
  const [totalAffiliatePages, setTotalAffiliatePage] = useState(null);

  const [teams, setTeams] = useState({});
  const [currentPage, setCurrentPage] = useState({});
  const [totalPages, setTotalPages] = useState({});

  const { signedInAsSuperAdmin, currentUsersAffiliateId, urls } = props;

  const teamInitialAttributes = {
    name: null,
    affiliate: null,
    users: [],
  };

  const fetchAffiliates = async page => {
    const { data } = await httpClient.get(routes.affiliates.list({ page }));
    setAffiliates(data.affiliates);
    if (typeof data.pagination !== 'undefined') {
      setCurrentAffiliatePage(data.pagination.currentPage);
      setTotalAffiliatePage(data.pagination.totalPages);
    }
    data.affiliates.forEach(affiliate => {
      setTeams(prevState => {
        const newState = { ...prevState };
        newState[affiliate.id] = [];
        return newState;
      });
      setCurrentPage(prevState => {
        const newState = { ...prevState };
        newState[affiliate.id] = 1;
        return newState;
      });
      setTotalPages(prevState => {
        const newState = { ...prevState };
        newState[affiliate.id] = null;
        return newState;
      });
    });
  };

  const fetchTeams = async (page, searchParam, affiliateId, direction) => {
    const { data } = await httpClient.get(
      routes.teams.list({
        q: searchParam,
        page,
        affiliate_id: affiliateId,
        direction,
      }),
    );

    setTeams(prevState => {
      const newState = { ...prevState };
      newState[affiliateId] = data.teams;
      return newState;
    });

    if (typeof data.pagination !== 'undefined') {
      setCurrentPage(prevState => {
        const newState = { ...prevState };
        newState[affiliateId] = data.pagination.currentPage;
        return newState;
      });
      setTotalPages(prevState => {
        const newState = { ...prevState };
        newState[affiliateId] = data.pagination.totalPages;
        return newState;
      });
    }
  };

  const fetchTeamsByAffiliateWithDebounce = useRef(
    debounce(500, (affiliates, currentPage, searchParam, direction) => {
      affiliates.forEach(affiliate => {
        fetchTeams(
          currentPage[affiliate.id],
          searchParam,
          affiliate.id,
          direction,
        );
      });
    }),
  ).current;

  const fetchTeamsByAffiliates = () => {
    fetchTeamsByAffiliateWithDebounce(
      affiliates,
      currentPage,
      searchParam,
      sortDirection,
    );
  };

  const onSortDirectionChange = () => {
    if (sortDirection === null) {
      setSortDirection('up');
    } else if (sortDirection === 'up') {
      setSortDirection('down');
    } else {
      setSortDirection(null);
    }
  };

  useEffect(() => {
    fetchAffiliates();
  }, []);

  useEffect(() => {
    fetchTeamsByAffiliates();
  }, [affiliates, searchParam, sortDirection]);

  const deleteTeam = async teamId => {
    const response = await httpClient.delete(routes.teams.destroy({ teamId }));
    fetchTeamsByAffiliates();
    return response;
  };

  return (
    <div className="team-index">
      <div className="team-index__header">
        <div className="team-index__header-text">
          <I18nText id="team.header" />
        </div>
        <div className="team-index__breadcrumbs">
          <Breadcrumbs>
            <BreadcrumbItem linkUrl={urls.users}>
              <I18nText id="user.header" />
            </BreadcrumbItem>
            <BreadcrumbItem linkUrl={urls.teams}>
              <I18nText id="team.header" />
            </BreadcrumbItem>
          </Breadcrumbs>
        </div>
      </div>
      <div className="team-index__search-section">
        <div className="team-index__search-box-container">
          <div className="team-index__search-box">
            <Input
              value={searchParam}
              onChange={value => setSearchParam(value)}
              preIcon={<Icon name="search" />}
              placeholder={I18n.t('components.support.search')}
              fullWidth={true}
            />
          </div>
        </div>
        <div className="team-index__create-actions col-auto col-bleed">
          <a
            className="button button--v2 button--small team-index__create-actions-back-to-user-button"
            href={urls.users}
          >
            <I18nText id="team.back_to_users" />
          </a>
          <TeamModal
            signedInAsSuperAdmin={signedInAsSuperAdmin}
            currentUsersAffiliateId={currentUsersAffiliateId}
            fetchTeamsByAffiliates={fetchTeamsByAffiliates}
            isNewRecord={true}
            teamInitialAttributes={teamInitialAttributes}
            buttonClass="button button--v2 button--small button--primary"
            buttonText={<I18nText id="team.actions.create" />}
            header={<I18nText id="team.actions.create" />}
          />
        </div>
      </div>
      <div className="team-index__sort-section">
        <SortingPill
          size="small"
          sortDirection={sortDirection}
          parameterName={<I18nText id="team.sort_label.name" />}
          onClick={onSortDirectionChange}
        />
      </div>
      <div className="team-index__list">
        {affiliates.map(affiliate => (
          <React.Fragment key={affiliate.id}>
            {signedInAsSuperAdmin && (
              <div className="team-index__client-name">
                {affiliate.clientName}
              </div>
            )}
            <ListTable
              signedInAsSuperAdmin={signedInAsSuperAdmin}
              currentUsersAffiliateId={currentUsersAffiliateId}
              fetchTeamsByAffiliates={fetchTeamsByAffiliates}
              teams={teams[affiliate.id] || []}
              affiliateId={affiliate.id}
              searchParam={searchParam}
              sortDirection={sortDirection}
              currentPage={currentPage[affiliate.id]}
              totalPages={totalPages[affiliate.id]}
              setCurrentPage={setCurrentPage}
              fetchTeams={fetchTeams}
              deleteTeam={deleteTeam}
            />
          </React.Fragment>
        ))}
        {signedInAsSuperAdmin && (
          <PaginationWithTranslation
            currentPage={currentAffiliatePage}
            totalPages={totalAffiliatePages}
            onPaginationClick={value => {
              setCurrentAffiliatePage(value);
              fetchAffiliates(value);
            }}
          />
        )}
      </div>
    </div>
  );
};

TeamIndex.propTypes = {
  signedInAsSuperAdmin: PropTypes.bool.isRequired,
  currentUsersAffiliateId: PropTypes.number.isRequired,
  urls: PropTypes.shape({
    users: PropTypes.string,
    teams: PropTypes.string,
  }).isRequired,
};

export default TeamIndex;
